import { Component, OnInit, OnDestroy, Input, SimpleChanges, Output, EventEmitter, ChangeDetectorRef, ViewChild, TemplateRef, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ChartConfiguration } from 'chart.js';
import { combineLatest, map, Observable, Subject, takeUntil, take, tap, catchError, Subscription, BehaviorSubject, EMPTY, debounceTime } from 'rxjs';
import { CampaignSegment } from '../../model/campaign-segment.model';
import { Category } from '../../model/category.model';
import { smallSalesChartOptions } from '../../model/chart';
import { DicoEntry } from '../../model/dico-entry.model';
import { MetaCategory } from '../../model/meta-category.model';
import { TaskState } from '../../model/task.state';
import { DicoService } from '../../service/dico.service';
import { Product, ProductsState, ProductsStore } from '../pages/dashboard/product.store';
import { EmailStore, Template } from '../pages/generator/email.store';
import { Enterprise, UserStore } from '../../store/user.store';
import { Event } from '../../../deebr/service/event.service';
import { ProductsService } from 'src/app/deebr/service/product.service';
import { TemplateModalComponent } from '../template-modal/template-modal.component';
import { DownloadService } from 'src/app/deebr/service/download.service';
import { ProductDisposition, DashboardProduct } from '../drag-drop-products/drag-drop-products.component';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { GeneratorService } from 'src/app/deebr/service/generator.service';
import { ScrappedImagesService } from 'src/app/deebr/service/scrapped-images.service';
import { SharedProductService } from 'src/app/deebr/service/shared-product.service';
import { Options } from '@angular-slider/ngx-slider';
import { FormControl } from '@angular/forms';
import { forkJoin, from, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

enum Direction {
  Asc = 'asc',
  Desc = 'desc',
}

export type TableDataLine = {
  category: Category;
  id: number;
  label?: string;
  real?: number;
  realNMinusOne?: number;
  evolution?: number | null;
  evolutionNMinusOne?: number | null;
  stock?: number;
  dispo?: number;
  forecast?: number;
  data?: ChartConfiguration['data'];
  showProducts: boolean;
  isSegment?: boolean;
};

type ColumnKey = keyof TableDataLine;

interface Filter {
  is_deebr: boolean;
  is_new: boolean;
  has_visual: boolean;
  is_promo: boolean;
}

interface EmailPayload {
  metaCategoryId: number;
  ids: (string | null)[];
  theme: string;
  templateId: number;
  visuel_url?: string[];  // Notez le '?' qui rend cette propriété optionnelle
  task_id?: string;     // De même ici
}

export enum StockOrDispo {
  Stock = 'stock',
  Dispo = 'dispo',
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  providers: [ProductsStore],
  standalone: false
})

export class TableComponent implements OnInit, OnDestroy {
  public readonly productStore = inject(ProductsStore);
  private readonly userStore = inject(UserStore);
  private readonly emailStore = inject(EmailStore);
  public forms!: FormGroup;
  public nbProducts: Record<Template['id'], Template['nb_produits']> = {};
  public nbVisuelsPrincipaux: Record<Template['id'], Template['nb_visuels_principaux']> = {};
  public matrice: Record<Template['id'], Template['products_disposition']> = {};
  public filter: Filter = { is_deebr: false, is_new: false, has_visual: false, is_promo: false };
  private salesChartsDataList: any = [];
  private _showChoice: boolean = false;
  public selectionDragAndDrop: {
    instance_id: string;
    product_id: string | null;
    image_url_1: string;
    scenario_image_url: string | undefined;
    ordre_generation: number;
    width: number;
    height: number;
    url: string;
    label?: string;
    discount_pct?: string | number;
  }[] = [];
  public scenarioImageIndex: { [productId: string]: number } = {};
  public productsDisposition: ProductDisposition[] = [];
  //TODO: peut-etre changer le product ProductDisposition[] en {id: number, image_url_1: string}[] etc
  lines$: BehaviorSubject<TableDataLine[]> = new BehaviorSubject<TableDataLine[]>([]);
  private searchSubject$ = new Subject<{ categoryId: Category['id']; value: string | null }>();
  private idsExclusionSubject$ = new Subject<{ categoryId: Category['id']; value: string | null }>();
  selectedImageUrl!: string;
  taskId!: string;
  imageUrls: string[] = [];
  choiceToastSubscription: Subscription;
  currentCategoryId?: number;
  tempCategoryId?: number;
  selectedTemplateId: number | null = null;
  selectedTemplateName: string | null = null;
  isSaving: boolean = false
  exclude_ids: string | null = null;
  activeFilter: 'deebr' | 'new' | 'visuel' | 'promo' | null = null;
  sortKey: 'ventes' | 'stock' | 'pageviews' | 'score' | null = 'ventes';
  searchTerm: string | null = null;
  sortDirection: 'ASC' | 'DESC' = 'DESC';
  startatOperator: string = '<=';
  endatOperator: string = '>=';
  startDate: string | null = null;
  endDate: string | null = null;
  resetKey = 0;
  lenselection: number = 0;
  panelState: string = 'out';
  unselectedProductsCount: number = 0;
  selectedProductsCount: number = 0;
  smallSalesChartOptions: any = smallSalesChartOptions;
  products$: Observable<ProductsState['products']> = this.productStore.selectProducts();
  categoryId$: Subject<Category['id']> = new Subject();
  isSaving$: Observable<boolean> = this.emailStore.selectProductSaving();
  destroy$: Subject<void> = new Subject();
  TaskState: typeof TaskState = TaskState;
  hoveringProduct: any = null;
  public maxItemCount: number = 0;
  showFromMatrice: boolean = false;
  replaceVenteWithDevis = false;
  images: { [key: string]: string[] } = {};
  private scrappedImagesSubscription!: Subscription;
  clickedProduct: Product | null = null;
  // instanceIds : placements retirés, pour que l'enfant purge leur état de carrousel.
  resetProductIndex: { categoryId: Category['id'], productId: string | null, instanceIds: string[] } | null = null;

  minStock: number = 0;
  maxStock: number = 1000;
  options: Options = {
    floor: 0,
    ceil: 1000,
    step: 1,
    translate: (value: number): string => {
      return value === 1000 ? '1000+' : value.toString();
    }
  };
  private stockRangeTimeout: any;

  dialogRef: NbDialogRef<any> | undefined = undefined;
  @ViewChild('dialogSelectTemplate') dialogSelectTemplate!: TemplateRef<any>;
  @ViewChild('dialogNoPlace') dialogNoPlace!: TemplateRef<any>;
  @ViewChild('dialogErrorScrap') dialogErrorScrap!: TemplateRef<any>;

  @Input() searchControl!: FormControl;
  @Input() showTableControls: boolean = true;


  private _metaCategory!: MetaCategory;

  @Input()
  set metaCategory(value: MetaCategory) {
    if (this._metaCategory && this._metaCategory.id !== value.id) {
      this.clearSelection(); // Clear only if it's a new metaCategory
    }
    this._metaCategory = value;
  }

  get metaCategory(): MetaCategory {
    return this._metaCategory;
  }

  @Input()
  currentCategory?: Category | undefined;

  @Input()
  dicoEntries: DicoEntry[] = [];

  @Input()
  segments?: CampaignSegment[] | undefined;

  @Input()
  stockOrDispo: StockOrDispo = StockOrDispo.Stock;

  @Input()
  numberOfDays!: number;

  @Input()
  forecastDates: Date[] = [];

  @Input()
  searchString: string = '';

  @Input()
  canShowProduct: boolean = false;

  @Input()
  templates: Template[] = [];

  @Output()
  onRowClicked: EventEmitter<Category> = new EventEmitter();

  @ViewChild('templateModal') templateModal!: TemplateModalComponent;
  openModal() {
    this.templateModal.open(); // Assurez-vous que `templateModal` est correctement référencé
  }

  onBlur(): void {
    const textArea = document.querySelector('.textarea-resize');
    if (textArea) {
      textArea.scrollTop = 0;
    }
  }

  /**
   * Compteur d'identités de placement. Un simple entier suffit : l'unicité n'est
   * requise qu'à l'intérieur d'un conteneur Swapy, dont ce composant est le seul
   * propriétaire. Lisible en DevTools (data-swapy-item="pi7") et monotone.
   */
  private instanceSeq = 0;
  private newInstanceId(): string {
    return `pi${++this.instanceSeq}`;
  }

  /**
   * Construit des cases vides à partir du schéma du template, chacune avec son
   * identité de placement. Tri par ordre_generation : l'index du tableau devient
   * le numéro d'emplacement affiché ET la position envoyée au backend (produit_N),
   * et reste cohérent avec le tri appliqué par handleOrderChanges après un swap.
   */
  private buildEmptyDisposition(templateId: number): ProductDisposition[] {
    return [...this.matrice[templateId].schema]
      .sort((a, b) => a.ordre_generation - b.ordre_generation)
      .map(slot => ({
        ...slot,
        instance_id: this.newInstanceId(),
        product_id: null,
        image_url_1: '',
        scenario_image_url: '',
        label: '',
        discount_pct: undefined,
        categoryId: undefined,
      }));
  }

  initializeProductsDisposition(): void {
    const templateId = this.selectedTemplateId ?? 0;
    this.showFromMatrice = this.matrice[templateId].show;
    this.productsDisposition = this.buildEmptyDisposition(templateId);
  }

  /**
   * Unique point de projection productsDisposition -> selectionDragAndDrop.
   * Rafraîchit aussi l'index de sélection et les compteurs : ces derniers n'étaient
   * jusqu'ici mis à jour que par effet de bord de getTooltipText() évalué depuis le template.
   */
  private projectSelection(): void {
    this.selectionDragAndDrop = this.productsDisposition.map(p => ({
      instance_id: p.instance_id,
      product_id: p.product_id,
      image_url_1: p.image_url_1,
      scenario_image_url: p.scenario_image_url,
      ordre_generation: p.ordre_generation,
      width: p.width,
      height: p.height,
      url: p.url,
      label: p.label,
      discount_pct: p.discount_pct,
    }));
    this.rebuildSelectionIndex();
    this.selectedProductsCount = this.productsDisposition.filter(p => p.product_id !== null).length;
    this.unselectedProductsCount = this.productsDisposition.length - this.selectedProductsCount;
  }

  /** product_id -> numéros d'emplacement occupés (1-based, croissants). Lecture O(1) depuis le template. */
  private selectionByProduct = new Map<string, number[]>();

  private rebuildSelectionIndex(): void {
    const index = new Map<string, number[]>();
    this.productsDisposition.forEach((p, i) => {
      if (p.product_id === null) {
        return;
      }
      const slots = index.get(p.product_id);
      if (slots) {
        slots.push(i + 1);
      } else {
        index.set(p.product_id, [i + 1]);
      }
    });
    this.selectionByProduct = index;
  }

  onTemplateSelect(template: Template) {
    this.selectedTemplateId = template.id;
    this.selectedTemplateName = template.name;
    this.initializeProductsDisposition();
    this.projectSelection();
    this.maxItemCount = Object.keys(this.productsDisposition).length;
    // console.log("INITIALISATION PRODUCTS DISPOSITION: ", this.productsDisposition);
    // console.log("INITIALISATION SELECTION DRAG AND DROP: ", this.selectionDragAndDrop);
  }

  public showGenerator$: Observable<boolean> = this.userStore
    .selectEnterprise()
    .pipe(map((enterprise: Enterprise | null) => !!enterprise?.email_gen));

  public showPerfectstayElement$: Observable<boolean> = this.userStore
    .selectEnterprise()
    .pipe(
      map((enterprise: Enterprise | null) => {
        return ['airfrance', 'emirates', 'transavia'].includes(enterprise?.name?.toLowerCase() ?? '');
      })
    );

  private updateReplaceVenteWithDevis(enterprise: Enterprise | null): void {
    const name = enterprise?.name?.toLowerCase() ?? '';
    if (['airfrance', 'emirates', 'transavia'].includes(name)) {
      this.replaceVenteWithDevis = name === 'emirates';
    } else {

      this.replaceVenteWithDevis = false;
    }
    this.cdr.detectChanges(); // Trigger change detection
  }



  sorting: { key: string | undefined; direction: Direction | undefined } = {
    key: 'forecast',
    direction: Direction.Desc,
  };

  constructor(
    private formBuilder: FormBuilder,
    private dicoService: DicoService,
    private Event: Event,
    private cdr: ChangeDetectorRef,
    private readonly productsService: ProductsService,
    private readonly downloadService: DownloadService,
    private dialogService: NbDialogService,
    private generatorService: GeneratorService,
    private scrappedImagesService: ScrappedImagesService,
    private sharedProductService: SharedProductService,
  ) {
    this.lines$.subscribe(data => { });
    this.choiceToastSubscription = this.Event.imageChoiceEvent$.subscribe((toast: { imageUrls: string[], message: string, taskId: string }) => {
      if (toast) {
        this.imageUrls = toast.imageUrls;
        this.showChoice = true;
        this.taskId = toast.taskId;
      }
    });
  }

  get showChoice(): boolean {
    return this._showChoice;
  }

  set showChoice(value: boolean) {
    this._showChoice = value;
  }

  formatNumber(num?: number): string {
    if (num === undefined || num === null) {
      return 'N/A';  // Ou tout autre texte par défaut
    }
    return num.toLocaleString('fr-FR');
  }

  formatNumberToTwoDecimals(num?: number): any {
    if (num === undefined || num === null) {
      return 'N/A'; // Ou tout autre texte par défaut
    } else if (num === 0) {
      return 0;
    }
    return Number(num).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  toggleDragDropPanel() {
    this.panelState = this.panelState === 'out' ? 'in' : 'out';
    // Communiquer avec le composant app-drag-drop-products si nécessaire
  }

  exportProducts(categoryId: Category['id']) {
    if (this.currentCategoryId === undefined || this.currentCategoryId === null) {
      console.error("Aucune catégorie actuelle sélectionnée!");
      return;
    }

    this.productStore.state$.pipe(take(1)).subscribe(state => {
      const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId] || {};

      this.productsService.getProduct(
        this.currentCategoryId!,
        this.metaCategory.id,
        0,
        currentCategoryState.filter,
        currentCategoryState.sort,
        currentCategoryState.searchTerm,
        currentCategoryState.exclude_ids,
        null,
        currentCategoryState.startat,
        currentCategoryState.startatComparator,
        currentCategoryState.endat,
        currentCategoryState.endatComparator,
        currentCategoryState.stockMin || null,
        currentCategoryState.stockMax || null,
        true, // Indiquez que vous voulez exporter les données
        'blob' // Le type de réponse attendu pour un export
      ).pipe(
        tap((response: Blob) => {
          if (response.size === 0) {
            console.error("Aucun produit à exporter.");
            return;
          }
          response.text().then(text => {
            this.downloadService.saveAsCsv(text, 'products.csv');
          });
        }),
        catchError((error) => {
          console.error('An error occurred', error);
          return EMPTY;
        })
      ).subscribe();
    });
  }

  loadAllProductsForCategory(metaCategoryId: number, categoryId: Category['id']): Observable<Product[]> {
    return new Observable<Product[]>(observer => {
      this.productsService.getProduct(categoryId, metaCategoryId, 0, null, { key: 'ventes', direction: 'DESC' }, null,)
        .pipe(
          take(1),
        )
        .subscribe({
          next: (products) => {
            observer.next(products); // Pousse les produits dans l'Observable
            observer.complete(); // Complète l'Observable
          },
          error: (err) => {
            observer.error(err); // En cas d'erreur, propage l'erreur
            observer.complete(); // Complète l'Observable
          }
        });
      // Pas besoin de retourner une fonction de désinscription ici,
      // car `take(1)` garantit que l'Observable est complété après le premier événement.
    });
  }

  handleCloseRequest() {
    this.showChoice = false;
  }

  ngOnInit(): void {
    this.userStore.selectEnterprise().subscribe({
      next: (enterprise) => {
        this.updateReplaceVenteWithDevis(enterprise);
      },
      error: (err) => {
        console.error('Error:', err);
      },
      complete: () => {

      }
    });
    this._initForm();
    this.showPerfectstayElement$.subscribe();
    this.setLineGraphsData();
    this.buildSortedList();
    this._initProductsLoader();
    this.lines$.subscribe(data => { });
    this.setLineGraphsData();
    this.buildSortedList();
    this._initProductsLoader();
    this.lines$.subscribe(data => { });

    // Debounced product search
    this.searchSubject$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(({ categoryId, value }) => {
      this.executeSearch(categoryId, value);
    });

    // Debounced IDs exclusion
    this.idsExclusionSubject$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(({ categoryId, value }) => {
      this.executeIdsExclusion(categoryId, value);
    });

    // Subscribe to the scrapped images service
    this.scrappedImagesSubscription = this.scrappedImagesService.currentProducts.subscribe((productMap) => {
      productMap.forEach((urls, productId) => {
        if (!this.images[productId!]) {
          this.images[productId!] = [];
        }

        // Add only unique URLs to prevent duplication
        urls.forEach((url: string) => {
          if (!this.images[productId!].includes(url) && url !== "no image found") {
            this.images[productId!].push(url);
          }
        });
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('searchString' in changes) {
      this.buildSortedList();
    }

    if ('dicoEntries' in changes) {
      this.setLineGraphsData();
      this.buildSortedList();
    }

    if ('segments' in changes) {
      this.buildSortedList();
    }

    if ('templates' in changes) {
      this.nbProducts = (this.templates || []).reduce(
        (res, template) => ({ ...res, [template.id]: template.nb_produits }),
        {},
      );

      this.matrice = (this.templates || []).reduce(
        (res, template) => ({ ...res, [template.id]: template.products_disposition }),
        {},
      );

      this.nbVisuelsPrincipaux = (this.templates || []).reduce(
        (res, template) => ({ ...res, [template.id]: template.nb_visuels_principaux }),
        {},
      );

    }
  }

  onImageSelected(event: { imageUrl: any, taskId: string, selectedRatio?: number, wantGif?: boolean, selectedLogo?: string }) {
    // console.log('onImageSelected', event.selectedRatio)
    this.validateSelection(event.imageUrl, event.taskId, event.selectedRatio, event.wantGif, event.selectedLogo);
  }

  ngOnDestroy(): void {
    this.destroy$.next(void 0);
    this.destroy$.complete();
    this.choiceToastSubscription.unsubscribe();
  }

  changeSorting(key: string) {
    if (this.sorting.key === key) {
      if (this.sorting.direction === Direction.Asc) {
        this.sorting.direction = Direction.Desc;
      } else if (this.sorting.direction === Direction.Desc) {
        this.sorting = { direction: undefined, key: undefined };
      } else {
        this.sorting.direction = Direction.Asc;
      }
    } else {
      this.sorting.key = key;
      this.sorting.direction = Direction.Asc;
    }
    this.buildSortedList();
  }

  getTooltipText(): string {
    if (this.selectedTemplateId === null) {
      return 'Veuillez choisir un template pour générer votre email';
    } else {
      this.selectedProductsCount = this.productsDisposition.filter(p => p.product_id !== null)?.length || 0;
      this.unselectedProductsCount = this.productsDisposition.filter(p => p.product_id === null)?.length || 0;

      if (this.unselectedProductsCount > 0) {
        return 'Veuillez sélectionner ' + this.unselectedProductsCount + ' produit(s) pour pouvoir générer votre email';
      } else if (this.unselectedProductsCount < 0) {
        return 'Veuillez supprimer ' + (-this.unselectedProductsCount) + ' produit(s) ou changer de template pour pouvoir générer votre email';
      }
    }
    return '';
  }

  tooltipDisabled(): boolean {
    return this.selectedTemplateId !== null && this.productsDisposition.filter(p => p.product_id !== null).length === this.nbProducts[this.selectedTemplateId] && !this.isSaving;
  }

  filterBy(categoryId: Category['id'], filterValue: 'deebr' | 'new' | 'visuel' | 'promo' | null): void {
    this.productStore.state$.pipe(
      take(1),
      tap(state => {
        const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId] || {};
        // Mise à jour de l'état avec le nouveau filtre et réinitialisation de la page
        this.productStore.patchState({
          ...state,
          products: {
            ...state.products,
            [this.metaCategory.id]: {
              ...state.products[this.metaCategory.id],
              [categoryId]: {
                ...currentCategoryState,
                filter: filterValue,
                entities: [],
                page: 1, // Réinitialiser la pagination à la première page lors de l'application d'un nouveau filtre
              },
            },
          },
        });
        this.productStore.getProduct({
          metaCategoryId: this.metaCategory.id,
          categoryId: categoryId,
          page: 1, // S'assurer de demander la première page de résultats
          filter: filterValue,
          sort: currentCategoryState.sort, // Conserver le tri actuel
          searchTerm: currentCategoryState.searchTerm,
          exclude_ids: currentCategoryState.exclude_ids,
          startat: currentCategoryState.startat,
          startatComparator: currentCategoryState.startatComparator,
          endat: currentCategoryState.endat,
          endatComparator: currentCategoryState.endatComparator,
        });
      })
    ).subscribe();
  }

  public clearSelection(): void {
    this.productsDisposition = this.productsDisposition.map(p => ({
      ...p,
      product_id: null,
      image_url_1: '',
      scenario_image_url: '',
      // Sans ces trois-là, une case vidée garde son badge remise / son libellé.
      label: '',
      discount_pct: undefined,
      categoryId: undefined,
      // instance_id volontairement conservé : il est re-frappé au prochain placement.
    }));
    this.scrapeRequested.clear();
    this.projectSelection();
  }

  resetFilter(categoryId: Category['id']): void {
    this.activeFilter = null;
    this.sortKey = null;
    this.searchTerm = '';
    this.exclude_ids = null;
    this.sortDirection = 'DESC';
    this.startatOperator = '<=';
    this.endatOperator = '>=';
    this.startDate = '';
    this.endDate = '';
    this.minStock = 0;  // Reset min stock
    this.maxStock = 1000;  // Reset max stock
    this.cdr.detectChanges();
    this.productStore.state$.pipe(
      take(1),
      tap(state => {
        const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId] || {};
        this.productStore.patchState({
          ...state,
          products: {
            ...state.products,
            [this.metaCategory.id]: {
              ...state.products[this.metaCategory.id],
              [categoryId]: {
                ...currentCategoryState,
                filter: this.activeFilter,
                sort: { key: 'ventes', direction: this.sortDirection },
                searchTerm: this.searchTerm,
                exclude_ids: this.exclude_ids,
                startat: this.startDate,
                startatComparator: this.startatOperator,
                endat: this.endDate,
                endatComparator: this.endatOperator,
                stockMin: null,
                stockMax: null,
                entities: [],
                page: 1,
              },
            },
          },
        });
        this.productStore.getProduct({
          metaCategoryId: this.metaCategory.id,
          categoryId: categoryId,
          filter: this.activeFilter,
          sort: { key: 'ventes', direction: this.sortDirection },
          searchTerm: this.searchTerm,
          exclude_ids: this.exclude_ids,
          startat: this.startDate,
          startatComparator: this.startatOperator,
          endat: this.endDate,
          endatComparator: this.endatOperator,
          page: 1,
        });
      })
    ).subscribe();
  }

  sortBy(categoryId: Category['id'], sortKey: 'ventes' | 'stock' | 'pageviews' | 'score'): void {
    this.sortDirection = 'DESC'; // Assumption: sorting direction is always 'DESC' for simplicity
    this.productStore.state$.pipe(
      take(1),
      tap(state => {
        const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId] || {};

        const updatedCategoryState = {
          ...currentCategoryState,
          sort: {
            key: sortKey,
            direction: this.sortDirection,
          },
          entities: [],
          page: 1,
        };

        this.productStore.patchState({
          ...state,
          products: {
            ...state.products,
            [this.metaCategory.id]: {
              ...state.products[this.metaCategory.id],
              [categoryId]: updatedCategoryState,
            },
          },
        });

        // Appel à getProduct avec les nouveaux critères de tri et de pagination
        this.productStore.getProduct({
          metaCategoryId: this.metaCategory.id,
          categoryId: categoryId,
          page: 1,
          filter: updatedCategoryState.filter,
          sort: { key: sortKey, direction: this.sortDirection },
          searchTerm: updatedCategoryState.searchTerm,
          exclude_ids: updatedCategoryState.exclude_ids,
          startat: updatedCategoryState.startat,
          startatComparator: updatedCategoryState.startatComparator,
          endat: updatedCategoryState.endat,
          endatComparator: updatedCategoryState.endatComparator,
        });
      })
    ).subscribe();
  }

  public handleStartComparatorChange(startatOperator: string): void {
    this.startatOperator = startatOperator

    if (!this.currentCategoryId) {
      console.error("Aucune catégorie actuelle sélectionnée!");
      return;
    }

    if (this.startDate) {
      const formattedEndDate = this.endDate ? this.formatDate(new Date(this.endDate)) : this.endDate;
      this.filterByDate(this.currentCategoryId, this.formatDate(new Date(this.startDate)), this.startatOperator, formattedEndDate, this.endatOperator);
    }
  }

  public handleEndComparatorChange(endatOperator: string): void {
    this.endatOperator = endatOperator

    if (!this.currentCategoryId) {
      console.error("Aucune catégorie actuelle sélectionnée!");
      return;
    }

    if (this.endDate) {
      const formattedStartDate = this.startDate ? this.formatDate(new Date(this.startDate)) : this.startDate;
      this.filterByDate(this.currentCategoryId, formattedStartDate, this.startatOperator, this.formatDate(new Date(this.endDate)), this.endatOperator);
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    let month = (date.getMonth() + 1).toString();
    let day = date.getDate().toString();

    // Ajoute un zéro devant le mois/jour s'il est inférieur à 10 pour conserver le format mm/dd
    month = month.length < 2 ? '0' + month : month;
    day = day.length < 2 ? '0' + day : day;

    return `${year}-${month}-${day}`;
  }

  public handleStartDateChange(date: Date): void {
    if (!this.currentCategoryId) {
      console.error("Aucune catégorie actuelle sélectionnée!");
      return;
    }

    this.startDate = this.formatDate(date);
    const formattedEndDate = this.endDate ? this.formatDate(new Date(this.endDate)) : this.endDate;
    this.filterByDate(this.currentCategoryId, this.startDate, this.startatOperator, formattedEndDate, this.endatOperator);
  }

  public handleEndDateChange(date: Date): void {
    if (!this.currentCategoryId) {
      console.error("Aucune catégorie actuelle sélectionnée!");
      return;
    }

    this.endDate = this.formatDate(date);
    const formattedStartDate = this.startDate ? this.formatDate(new Date(this.startDate)) : this.startDate;
    this.filterByDate(this.currentCategoryId, formattedStartDate, this.startatOperator, this.endDate, this.endatOperator);
  }

  public handleStartDateInputChange(): void {
    if (!this.currentCategoryId) {
      console.error("Aucune catégorie actuelle sélectionnée !");
      return;
    }

    this.startDate = null;
    this.filterByDate(this.currentCategoryId, this.startDate, this.startatOperator, this.endDate, this.endatOperator);
  }

  public handleEndDateInputChange(): void {
    if (!this.currentCategoryId) {
      console.error("Aucune catégorie actuelle sélectionnée !");
      return;
    }

    this.endDate = null;
    this.filterByDate(this.currentCategoryId, this.startDate, this.startatOperator, this.endDate, this.endatOperator);
  }

  public filterByDate(
    categoryId: Category['id'],
    startDate: string | null,
    startatOperator: string | null,
    endDate: string | null,
    endatOperator: string | null
  ): void {
    this.productStore.state$.pipe(
      take(1),
      tap(state => {
        const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId] || {};
        this.productStore.patchState({
          ...state,
          products: {
            ...state.products,
            [this.metaCategory.id]: {
              ...state.products[this.metaCategory.id],
              [categoryId]: {
                ...currentCategoryState,
                startat: startDate,
                startatComparator: startatOperator,
                endat: endDate,
                endatComparator: endatOperator,
                entities: [],
                page: 1, // Réinitialiser la pagination à la première page lors de l'application d'un nouveau filtre
              },
            },
          },
        });
        this.productStore.getProduct({
          metaCategoryId: this.metaCategory.id,
          categoryId: categoryId,
          page: 1,
          filter: currentCategoryState.filter,
          sort: currentCategoryState.sort,
          searchTerm: currentCategoryState.searchTerm,
          exclude_ids: currentCategoryState.exclude_ids,
          startat: startDate,
          startatComparator: startatOperator,
          endat: endDate,
          endatComparator: endatOperator,
        });
      })
    ).subscribe();
  }

  getSortText(sortKey: string): string {
    const sortTextMap: { [key: string]: string } = {
      'ventes': 'Ventes',
      'stock': 'Stock',
      'pageviews': 'Pages Vues',
      'score': 'Score',
    };
    return sortTextMap[sortKey] || '';
  }

  public loadMore(categoryId: Category['id']): void {
    this.productStore.state$.pipe(
      take(1),
      tap(state => {
        const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId];
        if (!currentCategoryState) return;

        const nextPage = (currentCategoryState.page ?? 1) + 1;

        const actionParams = {
          metaCategoryId: this.metaCategory.id,
          categoryId,
          page: nextPage,
          searchTerm: currentCategoryState.searchTerm ?? null,
          exclude_ids: currentCategoryState.exclude_ids ?? null,
          filter: currentCategoryState.filter ?? null,
          sort: currentCategoryState.sort,
          startat: currentCategoryState.startat ?? null,
          startatComparator: currentCategoryState.startatComparator ?? null,
          endat: currentCategoryState.endat ?? null,
          endatComparator: currentCategoryState.endatComparator ?? null,
        };

        this.productStore.patchState((s) => ({
          ...s,
          products: {
            ...s.products,
            [this.metaCategory.id]: {
              ...s.products[this.metaCategory.id],
              [categoryId]: {
                ...currentCategoryState,
                page: nextPage,
                loading: TaskState.InProgress,
              },
            },
          },
        }));

        this.productStore.getProduct(actionParams as any);
      })
    ).subscribe();
  }


  public onSearch(categoryId: Category['id'], event: any): void {
    this.searchTerm = event.target.value;
    this.searchSubject$.next({ categoryId, value: this.searchTerm });
  }

  private executeSearch(categoryId: Category['id'], searchTerm: string | null): void {
    this.productStore.state$.pipe(
      take(1),
      tap(state => {
        const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId] || {};
        const updatedCategoryState = {
          ...currentCategoryState,
          searchTerm: searchTerm,
          entities: [],
          page: 1,
        };
        this.productStore.patchState({
          ...state,
          products: {
            ...state.products,
            [this.metaCategory.id]: {
              ...state.products[this.metaCategory.id],
              [categoryId]: updatedCategoryState,
            },
          },
        });
        this.productStore.getProduct({
          metaCategoryId: this.metaCategory.id,
          categoryId: categoryId,
          page: 1,
          filter: updatedCategoryState.filter,
          sort: currentCategoryState.sort,
          searchTerm: searchTerm,
          exclude_ids: updatedCategoryState.exclude_ids,
          startat: updatedCategoryState.startat,
          startatComparator: updatedCategoryState.startatComparator,
          endat: updatedCategoryState.endat,
          endatComparator: updatedCategoryState.endatComparator,
        });
      })
    ).subscribe();
  }

  public onIdsExclusion(categoryId: Category['id'], event: any): void {
    this.exclude_ids = event.target.value;
    this.idsExclusionSubject$.next({ categoryId, value: this.exclude_ids });
  }

  private executeIdsExclusion(categoryId: Category['id'], excludeIds: string | null): void {
    this.productStore.state$.pipe(
      take(1),
      tap(state => {
        const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId] || {};
        const updatedCategoryState = {
          ...currentCategoryState,
          exclude_ids: excludeIds,
          entities: [],
          page: 1,
        };
        this.productStore.patchState({
          ...state,
          products: {
            ...state.products,
            [this.metaCategory.id]: {
              ...state.products[this.metaCategory.id],
              [categoryId]: updatedCategoryState,
            },
          },
        });
        this.productStore.getProduct({
          metaCategoryId: this.metaCategory.id,
          categoryId: categoryId,
          page: 1,
          filter: updatedCategoryState.filter,
          sort: currentCategoryState.sort,
          searchTerm: currentCategoryState.searchTerm,
          exclude_ids: excludeIds,
          startat: updatedCategoryState.startat,
          startatComparator: updatedCategoryState.startatComparator,
          endat: updatedCategoryState.endat,
          endatComparator: updatedCategoryState.endatComparator,
        });
      })
    ).subscribe();
  }

  /** Numéros d'emplacement (1-based) occupés par ce produit, croissants. [] si non sélectionné. */
  public getSelectionNumbers(productId: string): number[] {
    return this.selectionByProduct.get(productId) ?? [];
  }

  /** Nombre d'exemplaires de ce produit actuellement placés. */
  public getSelectionCount(productId: string): number {
    return this.getSelectionNumbers(productId).length;
  }

  /** Numéros pré-joints pour l'affichage sur la vignette : "2 · 5 · 7". */
  public getSelectionLabel(productId: string): string {
    return this.getSelectionNumbers(productId).join(' · ');
  }

  /** Reste-t-il une case libre pour un exemplaire supplémentaire ? */
  public hasFreeSlot(): boolean {
    return this.productsDisposition.some(p => p.product_id === null);
  }

  changeCategory(categoryId: Category['id']): void {
    this.currentCategoryId = categoryId;

    // console.log("Catégorie changée : ", categoryId);
  }

  pushScrappedProducts(id: string | null, url_image: string, url_html: string): void {
    this.scrappedImagesService.startScraping(id, url_image, url_html, this.dialogErrorScrap);
  }
  public hasSelectedProductInCategory(categoryId: number): boolean {
    // categoryId survit désormais à l'aller-retour orderChanged : lecture directe
    // plutôt qu'une recherche indirecte par product_id.
    return this.productsDisposition.some(p => p.product_id !== null && p.categoryId === categoryId);
  }

  /** Produits pour lesquels un scraping d'images a déjà été demandé (une fois par produit, pas par exemplaire). */
  private scrapeRequested = new Set<string>();

  /** Indices des cases occupées par ce produit, dans l'ordre du tableau. */
  private indexesOf(productId: string): number[] {
    const indexes: number[] = [];
    this.productsDisposition.forEach((p, i) => {
      if (p.product_id === productId) {
        indexes.push(i);
      }
    });
    return indexes;
  }

  /** Vide une case. Conserve instance_id : il sera re-frappé au prochain placement. */
  private clearSlotAt(index: number): void {
    this.productsDisposition[index] = {
      ...this.productsDisposition[index],
      product_id: null,
      image_url_1: '',
      scenario_image_url: '',
      label: '',
      discount_pct: undefined,
      categoryId: undefined,
    };
  }

  /** Place un exemplaire supplémentaire du produit dans la première case libre. */
  public addProductInstance(categoryId: Category['id'], product: Product): void {
    this.showPerfectstayElement$.pipe(take(1)).subscribe(isPerfectStay => {
      if (this.selectedTemplateId === null && !isPerfectStay) {
        this.dialogRef = this.dialogService.open(this.dialogSelectTemplate);
        return;
      }

      const freeIndex = this.productsDisposition.findIndex(p => p.product_id === null);
      if (freeIndex === -1) {
        this.dialogRef = this.dialogService.open(this.dialogNoPlace);
        this.changeCategory(categoryId);
        return;
      }

      const scenarioUrl = product.scenario_image_url && product.scenario_image_url !== '' && product.scenario_image_url !== -1
        ? (typeof product.scenario_image_url === 'string' ? product.scenario_image_url.split(',')[0].trim() : String(product.scenario_image_url))
        : null;

      this.productsDisposition[freeIndex] = {
        ...this.productsDisposition[freeIndex],
        instance_id: this.newInstanceId(),
        product_id: product.product_id,
        image_url_1: scenarioUrl || product.image_url_1,
        scenario_image_url: product.scenario_image_url,
        url: product.url,
        categoryId,
        label: product.label,
        discount_pct: product.discount_pct,
      };
      this.projectSelection();

      // Le pool d'images est commun au produit : un seul scraping, même pour N exemplaires.
      // Le Set protège aussi de deux clics rapides avant que this.images ne soit rempli.
      if (!this.scrapeRequested.has(product.product_id)
        && (!this.images[product.product_id] || this.images[product.product_id].length === 0)) {
        this.scrapeRequested.add(product.product_id);
        this.clickedProduct = product;
        this.sharedProductService.addProduct(product);
        this.pushScrappedProducts(product.product_id, product.image_url_1, product.url);
      }

      this.changeCategory(categoryId);
    });
  }

  /**
   * Retire l'exemplaire occupant le plus grand numéro d'emplacement, c'est-à-dire
   * le dernier numéro affiché sur la vignette (WYSIWYG).
   */
  public removeLastProductInstance(categoryId: Category['id'], productId: string): void {
    const indexes = this.indexesOf(productId);
    if (indexes.length === 0) {
      return;
    }
    const target = indexes[indexes.length - 1];
    this.resetProductIndex = {
      categoryId,
      productId,
      instanceIds: [this.productsDisposition[target].instance_id],
    };
    this.clearSlotAt(target);
    this.projectSelection();
    this.changeCategory(categoryId);
  }

  /** Retire tous les exemplaires du produit. */
  public removeAllProductInstances(categoryId: Category['id'], productId: string): void {
    const indexes = this.indexesOf(productId);
    if (indexes.length === 0) {
      return;
    }
    this.resetProductIndex = {
      categoryId,
      productId,
      instanceIds: indexes.map(i => this.productsDisposition[i].instance_id),
    };
    indexes.forEach(i => this.clearSlotAt(i));
    this.scrapeRequested.delete(productId);
    this.projectSelection();
    this.changeCategory(categoryId);
  }

  /** Clic sur la vignette : sélectionne si aucun exemplaire, retire tout sinon. */
  public onCardClick(categoryId: Category['id'], product: Product): void {
    if (this.getSelectionCount(product.product_id) > 0) {
      this.removeAllProductInstances(categoryId, product.product_id);
    } else {
      this.addProductInstance(categoryId, product);
    }
  }

  /** Case à cocher : cocher place un exemplaire, décocher les retire tous. */
  public onSelectionCheckboxChange(categoryId: Category['id'], product: Product, checked: boolean): void {
    if (checked) {
      this.addProductInstance(categoryId, product);
    } else {
      this.removeAllProductInstances(categoryId, product.product_id);
    }
  }

  closeDialog() {
    this.dialogRef?.close();
  }

  hasSelectedProducts(): boolean {
    return this.productsDisposition && this.productsDisposition.filter(p => p.product_id === null) && this.productsDisposition.filter(p => p.product_id !== null).length > 0;
  }

  hasfilter(categoryId: Category['id']): any {
    return this.activeFilter || this.startDate || this.endDate || this.searchTerm || this.exclude_ids || this.minStock > 0 || this.maxStock < 1000 || this.sortKey;
  }

  public isProductSelected(productId: string): boolean {
    return this.selectionByProduct.has(productId);
  }

  handleOrderChanges(event: { updatedProducts: ProductDisposition[] }): void {
    // Tri sur une copie : l'ancienne version triait en place le tableau qu'elle
    // venait d'assigner à productsDisposition, mutant donc l'ordre du parent — et
    // donc l'ordre des ids envoyés au backend — comme effet de bord d'une projection.
    this.productsDisposition = [...event.updatedProducts].sort((a, b) => a.ordre_generation - b.ordre_generation);
    this.projectSelection();
    // console.log("handleorder PRODUCTS DISPOSITION: ", this.productsDisposition);
    // console.log("handleorder selectionDragAndDrop: ", this.selectionDragAndDrop);

  }

  isUploading: boolean = false;

  applyDiscount: boolean = true;
  /** Lien unique pour tous les visuels produits : désactivé par défaut. */
  applyGlobalLink: boolean = false;
  globalLinkUrl: string = '';

  public validateSelection(selectedImageUrl?: any, taskId?: string, selectedRatio?: number, wantGif?: boolean, selectedLogo?: string): void {

    this.isUploading = true;

    // On fige l'appariement : forkJoin est asynchrone et le panneau de drag-and-drop
    // reste actif pendant l'upload. Un swap entre-temps remplacerait productsDisposition
    // par un nouveau tableau, et les ids ne correspondraient plus aux URLs uploadées.
    const snapshot = [...this.productsDisposition];

    // 1. On prépare la liste des uploads à faire
    const uploadObservables = snapshot.map((product, index) => {
      const originalUrl = product.image_url_1;

      // Si l'URL est vide, on renvoie null tout de suite
      if (!originalUrl) return of(null);

      // Fonction pour convertir l'URL en Fichier
      const filePromise = async () => {
        const proxyUrl = `${environment.backendUrl}/api/proxy-image/?url=${encodeURIComponent(originalUrl)}&width=9999`;
        const response = await fetch(proxyUrl);

        if (!response.ok) throw new Error('Erreur téléchargement front');

        const blob = await response.blob();
        // On crée un nom de fichier unique
        const filename = `product_${product.product_id}_${index}.jpg`;
        return new File([blob], filename, { type: 'image/jpeg' });
      };

      // On transforme la Promise en Observable et on enchaîne avec l'upload
      return from(filePromise()).pipe(
        switchMap(file => this.generatorService.uploadFile(file)),
        catchError(err => {
          console.warn(`⚠️ Impossible d'uploader l'image front pour ${product.product_id}`, err);
          // Fallback : Si l'upload échoue, on garde l'URL originale (au moins on essaie)
          return of(originalUrl);
        })
      );
    });

    // 2. On lance tous les uploads en parallèle
    // "forkJoin" attend que TOUT soit fini avant de continuer
    forkJoin(uploadObservables).subscribe({
      next: (newUrls) => {

        console.log("✅ Toutes les images ont été sécurisées :", newUrls);

        // 3. On construit le payload avec les NOUVELLES URLs
        const theme: string = this.forms.get('theme')?.value ?? '';
        const templateId: number = this.selectedTemplateId ?? 0;
        const nbVisuelsPrincipaux = this.nbVisuelsPrincipaux[templateId];

        const payload: any = {
          metaCategoryId: this.metaCategory.id,
          ids: snapshot.map(product => product.product_id),
          images_products_urls: newUrls,
          theme,
          templateId,
          nbVisuelsPrincipaux,
          apply_discount: this.applyDiscount,
          apply_global_link: this.applyGlobalLink,
          global_link_url: this.applyGlobalLink ? this.globalLinkUrl.trim() : '',
        };

        if (selectedImageUrl && taskId) {
          payload.visuel_url = selectedImageUrl;
          payload.task_id = taskId;
        }
        if (selectedRatio) {
          payload.selected_ratio = selectedRatio;
        }
        if (wantGif) {
          payload.wantGif = wantGif;
        }
        if (selectedLogo) {
          payload.selectedLogo = selectedLogo;
        }

        // 4. On envoie enfin au store
        this.emailStore.generateEmail(payload);
        this.isUploading = false;

      },
      error: (error) => {
        console.error("Erreur critique lors de la préparation des images", error);
        this.isUploading = false;
        // Au choix : on bloque ou on lance quand même avec les vieilles URL en secours
      }
    });
  }


  public getImageSrc(product: any): string {
    if (this.hoveringProduct === product && product.scenario_image_url && product.scenario_image_url !== '' && product.scenario_image_url !== -1) {
      const urls = this.getScenarioImageUrls(product);
      if (urls.length > 0) {
        const idx = this.scenarioImageIndex[product.product_id] || 0;
        return urls[idx] || urls[0];
      }
    }
    return product.image_url_1;
  }

  public getScenarioImageUrls(product: any): string[] {
    if (!product.scenario_image_url || product.scenario_image_url === '' || product.scenario_image_url === -1) {
      return [];
    }
    if (typeof product.scenario_image_url === 'string') {
      return product.scenario_image_url.split(',').map((u: string) => u.trim()).filter((u: string) => u.length > 0);
    }
    return [String(product.scenario_image_url)];
  }

  public prevScenarioImage(product: any, event: MouseEvent): void {
    event.stopPropagation();
    const urls = this.getScenarioImageUrls(product);
    const current = this.scenarioImageIndex[product.product_id] || 0;
    this.scenarioImageIndex[product.product_id] = (current - 1 + urls.length) % urls.length;
  }

  public nextScenarioImage(product: any, event: MouseEvent): void {
    event.stopPropagation();
    const urls = this.getScenarioImageUrls(product);
    const current = this.scenarioImageIndex[product.product_id] || 0;
    this.scenarioImageIndex[product.product_id] = (current + 1) % urls.length;
  }

  private buildSortedList() {
    const lines = this.dicoEntries
      .map((entry, key) => {
        return {
          category: entry.category,
          id: entry.category.id,
          label: entry.category.label,
          real: entry.real,
          realNMinusOne: entry.realNMinusOne,
          evolution: this.dicoService.getRatio(entry),
          evolutionNMinusOne: this.dicoService.getRatio(entry, true),
          stock: entry.stock,
          dispo: entry.dispo,
          forecast: entry.forecast,
          data: this.salesChartsDataList[key],
          showProducts: false,
          isSegment: (this.segments || []).some((segment) =>
            segment.categories.some(
              (category) =>
                category.category.id === entry.category.id &&
                (Array.isArray(segment.meta_category)
                  ? segment.meta_category.includes(entry.category.meta_category)
                  : segment.meta_category === entry.category.meta_category)
            )
          ),
        } as TableDataLine;
      })
      .filter((a: TableDataLine) => {
        if (!this.searchString) {
          return true;
        }
        return a.label
          ?.toLowerCase()
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .includes(this.searchString.normalize('NFD').replace(/\p{Diacritic}/gu, ''));
      })
      .sort((a: TableDataLine, b: TableDataLine) => {
        if (!this.sorting.key) {
          return a.label && b.label ? (a.label > b.label ? 0 : 1) : 1;
        } else {
          let aVal = a[this.sorting.key as ColumnKey];
          let bVal = b[this.sorting.key as ColumnKey];
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            if (this.sorting.direction === Direction.Asc) {
              return aVal - bVal;
            } else {
              return bVal - aVal;
            }
          }
          return 0;
        }
      });
    this.lines$.next(lines);
  }

  isActiveEntry(entry: TableDataLine): boolean {
    if (!this.currentCategory) {
      return false;
    }
    const categoryMatches = entry.id === this.currentCategory.id;
    const metaCategoryMatches = entry.category.meta_category === this.currentCategory.meta_category;

    return categoryMatches && metaCategoryMatches;
  }

  private setLineGraphsData() {
    this.salesChartsDataList = this.dicoEntries.map((entry) => {
      const ratio = this.dicoService.getRatio(entry, true) ?? 0;
      return {
        datasets: [
          {
            data: entry.evol.map((evo, key) => ({
              date: this.forecastDates[key].getTime(),
              purchase: evo,
            })),
            borderColor: ratio > 0 ? '#74cdcd' : '#F76284',
            pointBorderColor: ratio > 0 ? '#74cdcd' : '#F76284',
            pointHoverBorderColor: ratio > 0 ? '#74cdcd' : '#F76284',
          },
        ],
      };
    });
  }

  public toggleProducts(entry: TableDataLine): void {
    // console.log("tempCategoryId ", this.tempCategoryId); //! pour garder les filtres actifs lors de l'ouverture et fermeture des catégories
    const currentLines = this.lines$.getValue();
    this.resetFilter(entry.id); // Réinitialiser les filtres lors de l'ouverture d'une nouvelle catégorie

    // Fermer la catégorie précédemment ouverte si elle est différente de celle actuellement cliquée
    if (this.currentCategoryId !== null && this.currentCategoryId !== entry.id) {
      const previousIndex = currentLines.findIndex(line => line.id === this.currentCategoryId);
      if (previousIndex !== -1) {
        currentLines[previousIndex].showProducts = false;
      }
    }

    // Ouvrir la catégorie actuellement cliquée ou la fermer si elle était déjà ouverte
    const currentIndex = currentLines.findIndex(line => line.id === entry.id);
    if (currentIndex !== -1 && this.canShowProduct === true) {
      currentLines[currentIndex].showProducts = !currentLines[currentIndex].showProducts;
      this.currentCategoryId = currentLines[currentIndex].showProducts ? entry.id : undefined;
    }

    this.lines$.next([...currentLines]);

    if (this.selectedTemplateId !== null) {
      const alreadyHasSelectedProducts = this.productsDisposition.some(p => p.product_id !== null);

      if (!alreadyHasSelectedProducts) {
        this.showFromMatrice = this.matrice[this.selectedTemplateId].show;
        this.productsDisposition = this.buildEmptyDisposition(this.selectedTemplateId);
        this.projectSelection();
      }
    }


    if (currentLines[currentIndex].showProducts) {

      this.categoryId$.next(entry.id);
      setTimeout(() => {
        window.scrollTo({ top: 100, behavior: 'smooth' });
        const tableElement = document.getElementById(`table-id-${entry.id}`);
        if (tableElement) {
          const topPosition = tableElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: topPosition, behavior: 'smooth' });
        }
      }, 0);
    }

    this.onRowClicked.emit(entry.category);
    this.cdr.detectChanges();
  }

  private _initProductsLoader(): void {
    combineLatest([this.products$, this.categoryId$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([products, categoryId]) => {
        if (!products[this.metaCategory.id]?.[categoryId]) {
          this.productStore.getProduct({
            metaCategoryId: this.metaCategory.id,
            categoryId,
            page: 1,
            filter: null,
            sort: { key: 'ventes', direction: 'DESC' },
            searchTerm: null,
            exclude_ids: null,
            startat: '',
            startatComparator: '',
            endat: '',
            endatComparator: '',
          });
        } else {
          const { page, filter, sort, loading, searchTerm, exclude_ids } = products[this.metaCategory.id][categoryId];
          if (loading === TaskState.NotStarted) {
            this.productStore.getProduct({
              metaCategoryId: this.metaCategory.id,
              categoryId,
              page,
              filter,
              sort,
              searchTerm: searchTerm,
              exclude_ids: exclude_ids,
              startat: '',
              startatComparator: '',
              endat: '',
              endatComparator: '',
            });
          }
        }
      });
  }

  private _initForm(): void {
    this.forms = this.formBuilder.group({
      theme: this.formBuilder.control(''),
      templateId: this.formBuilder.control(this.templates?.[0]?.id, Validators.required),
    });
  }

  onStockRangeChange(categoryId: Category['id']): void {
    if (this.stockRangeTimeout) clearTimeout(this.stockRangeTimeout);

    this.stockRangeTimeout = setTimeout(() => {
      this.productStore.state$.pipe(
        take(1),
        tap(state => {
          const currentCategoryState = state.products[this.metaCategory.id]?.[categoryId] || {};

          // Normalize extremes to null (means “clear”)
          const normalizedStockMin = this.minStock === 0 ? null : this.minStock;
          const normalizedStockMax = this.maxStock === 1000 ? null : this.maxStock;

          const updatedCategoryState = {
            ...currentCategoryState,
            // keep UI range for display
            stockRange: { min: this.minStock, max: this.maxStock },
            // but ALSO persist normalized filters for API/state logic
            stockMin: normalizedStockMin,
            stockMax: normalizedStockMax,
            entities: [],
            page: 1,
          };

          this.productStore.patchState({
            ...state,
            products: {
              ...state.products,
              [this.metaCategory.id]: {
                ...state.products[this.metaCategory.id],
                [categoryId]: updatedCategoryState,
              },
            },
          });

          // Send normalized values to effect/service
          const params = {
            metaCategoryId: this.metaCategory.id,
            categoryId,
            page: 1,
            filter: updatedCategoryState.filter,
            sort: updatedCategoryState.sort,
            searchTerm: updatedCategoryState.searchTerm,
            exclude_ids: updatedCategoryState.exclude_ids,
            startat: updatedCategoryState.startat,
            startatComparator: updatedCategoryState.startatComparator,
            endat: updatedCategoryState.endat,
            endatComparator: updatedCategoryState.endatComparator,
            stockMin: normalizedStockMin,          // null when 0
            stockMax: normalizedStockMax,          // null when 1000
          };

          this.productStore.getProduct(params);
        })
      ).subscribe();
    }, 300);
  }

}
